import React, { forwardRef } from 'react'
import styled, { CSSObject } from '@emotion/styled'
import { colors, font, layering, radius, spacing } from '@mobi/component-library/Theme/Common'
import { SvgLazy } from '@mobi/component-library/Common/Svg/SvgLazy/SvgLazy'
import { unicodeToEmoji } from '@core/Areas/FollowMe/helpers/unicodeToEmojiConverter'
import type { FollowMeProfile } from '@core/Areas/FollowMe/types'
import { ProfileAvatar } from '../ProfileAvatar'
import TabtouchSealLogo from './assets/logo-seal.png'
import TiltedGiddyUpLogoSvg from './assets/giddy-up-logo-tilted.svg'
import TiltedTabTouchSvg from './assets/tabtouch-logo-tilted.svg'
import { ProfileCardStats } from './Components/ProfileCardStats'

const enum LocalConstants {
  ProfileCardBgConfigTabTouchClass = 'profile-card__bg_config_tabtouch',
  ProfileCardBgConfigEmojiClass = 'profile-card__background--emoji',
  ProfileCardBgConfigGiddyUpClass = 'profile-card__background--giddyup',

  TabTouchBGColor = 'radial-gradient(circle at 20% 45%, #9A4EFF 0%, #6A0EBE 40%)',
  GiddyUpBGColor = '#00509F',

  ProfileCardDisplayNameXs = 'profile-card__display_name_xs',
  ProfileCardDisplayNameSm = 'profile-card__display_name_sm',
  ProfileCardDisplayNameMd = 'profile-card__display_name_md',
  ProfileCardDisplayNameLg = 'profile-card__display_name_lg',
  ProfileCardDisplayNameXl = 'profile-card__display_name_xl',
}

export const ProfileCard = forwardRef<
  HTMLDivElement,
  { profile: Pick<FollowMeProfile, 'id' | 'avatar' | 'displayName' | 'color'> }
>(({ profile }, ref) => {
  const { displayName, avatar, color } = profile

  const isGiddyUp = avatar === 'GIDDYUP'
  const isTabTouch = avatar.includes('.png') && avatar !== 'GIDDYUP'
  const isSponsored = isTabTouch || isGiddyUp

  const modeClassName = isSponsored
    ? isTabTouch
      ? LocalConstants.ProfileCardBgConfigTabTouchClass
      : LocalConstants.ProfileCardBgConfigGiddyUpClass
    : LocalConstants.ProfileCardBgConfigEmojiClass

  const bg = `url('${
    isSponsored
      ? isTabTouch
        ? TiltedTabTouchSvg
        : TiltedGiddyUpLogoSvg
      : makeEmojiTile(unicodeToEmoji(avatar))
  }')`

  const profileNameSize = getDisplayNameFontSize(displayName.length)

  return (
    <CardWrapperStyled ref={ref}>
      <article>
        <div>
          <header>
            <div className='profile-card__seal'>
              <img src={TabtouchSealLogo} height={80} width={80} />
            </div>

            <ProfileBackground
              className={modeClassName}
              style={
                {
                  '--bg': bg,
                  background: isSponsored
                    ? isTabTouch
                      ? LocalConstants.TabTouchBGColor
                      : LocalConstants.GiddyUpBGColor
                    : color,
                } as React.CSSProperties
              }
            >
              <div>
                <ProfileAvatar
                  size='profile-card'
                  avatar={avatar}
                  color={color}
                  shouldUseBg={false}
                />
              </div>
            </ProfileBackground>

            <div className='profile-card__ribbon'>
              <h4 className={profileNameSize}>
                <span>{displayName}</span>
              </h4>
            </div>
          </header>

          {isSponsored && (
            <div className='profile-card__provided-sticker'>
              <div>
                <span>Provided by</span>
                <span>
                  <SvgLazy
                    width='7rem'
                    color={isGiddyUp ? '#002F7B' : '#5D01A4'}
                    name={isGiddyUp ? 'GiddyUpLogo' : 'TABtouchLogo'}
                  />
                </span>
              </div>
            </div>
          )}

          <div className='profile-card__stats' style={{ marginTop: isSponsored ? '2rem' : '7rem' }}>
            <ProfileCardStats id={profile.id} />
          </div>
        </div>
      </article>
    </CardWrapperStyled>
  )
})

ProfileCard.displayName = 'ProfileCard'

// ==============
// Local Helpers
// ==============

const makeEmojiTile = (char: string) =>
  `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y=".9em" font-size="70" fill="rgba(0,0,0,1)">${char}</text></svg>`
  )}`

const getDisplayNameFontSize = (chars: number): string => {
  if (chars <= 14) return LocalConstants.ProfileCardDisplayNameXs
  if (chars <= 18) return LocalConstants.ProfileCardDisplayNameSm
  if (chars <= 21) return LocalConstants.ProfileCardDisplayNameMd
  if (chars <= 23) return LocalConstants.ProfileCardDisplayNameLg

  return LocalConstants.ProfileCardDisplayNameXl
}

// ======
// Styles
// ======

const ProfileBackground = styled.div({
  position: 'relative',
  overflow: 'hidden',
  flex: 1,
  borderTopLeftRadius: radius.md,
  borderTopRightRadius: radius.md,
  boxShadow: 'inset 0 0 0.8rem rgba(0,0,0,0.5)',
  clipPath: 'polygon(0% 0%,100% 0%,100% 80%,0% 100%)',

  '::before': {
    opacity: 'var(--bg-opacity)',
    mixBlendMode: 'normal',
    content: '""',
    position: 'absolute',
    inset: 0,
    backgroundImage: 'var(--bg)',
    backgroundSize: 'var(--size, 60px 50px)',
    backgroundRepeat: 'repeat',
  },

  '::after': {
    opacity: 'var(--bg-opacity)',
    mixBlendMode: 'normal',
    content: '""',
    position: 'absolute',
    right: 0,
    bottom: 0,
    top: 'var(--top, -40px)',
    left: 'var(--left, -30px)',
    backgroundImage: 'var(--bg)',
    backgroundSize: 'var(--size, 60px 50px)',
    backgroundRepeat: 'repeat',
    transform: 'var(--rotate, rotate(2deg))',
    transformOrigin: 'top left',
    zIndex: -1,
  },

  [`&:not(.${LocalConstants.ProfileCardBgConfigEmojiClass})::before`]: {
    transform: 'var(--rotate, rotate(2deg))',
    backgroundPosition: '-12px 0',
  },

  // bg configs
  [`&.${LocalConstants.ProfileCardBgConfigEmojiClass}`]: {
    '--size': '80px 42px',
    '--top': '-30px',
    '--left': '-35px',
    '--rotate': 'unset',
    '--bg-opacity': 0.1,
  } as CSSObject,

  [`&.${LocalConstants.ProfileCardBgConfigGiddyUpClass}`]: {
    '--size': '60px 50px',
    '--top': '-25px',
    '--left': '-10px',
    '--rotate': 'rotate(1deg)',
    '--bg-opacity': 1,
  } as CSSObject,

  [`&.${LocalConstants.ProfileCardBgConfigTabTouchClass}`]: {
    '--size': '70px 60px',
    '--top': '-40px',
    '--left': '-30px',
    '--rotate': 'rotate(2deg)',
    '--bg-opacity': 1,
  } as CSSObject,

  [`&.${LocalConstants.ProfileCardBgConfigEmojiClass}::before, &.${LocalConstants.ProfileCardBgConfigEmojiClass}::after`]:
    { mixBlendMode: 'luminosity' as const },

  [`&.${LocalConstants.ProfileCardBgConfigGiddyUpClass}::before, &.${LocalConstants.ProfileCardBgConfigGiddyUpClass}::after,
    &.${LocalConstants.ProfileCardBgConfigTabTouchClass}::before, &.${LocalConstants.ProfileCardBgConfigTabTouchClass}::after`]:
    { mixBlendMode: 'normal' as const },

  [`&.${LocalConstants.ProfileCardBgConfigEmojiClass} > div`]: { maxWidth: '45%' },
  [`&:not(.${LocalConstants.ProfileCardBgConfigEmojiClass}) > div`]: {
    maxWidth: '55%',
    '> div > div': { bottom: '-1.5rem', left: '-1rem' },
  },
})

const CardWrapperStyled = styled.div({
  boxSizing: 'border-box',
  position: 'relative',
  width: '100%',
  maxWidth: '35rem',
  paddingInline: spacing.big,

  background: '#F2F2F2',
  padding: '1.3rem',
  borderRadius: '2.7rem',
  filter: 'drop-shadow(0 1px 4px rgba(0, 0, 0, 0.25))',
  border: '1px solid #FFF',
  pointerEvents: 'none',

  '*': { boxSizing: 'border-box' },

  '> article': {
    position: 'relative',
    borderRadius: radius.lgx2,
    background:
      'linear-gradient(171deg, #5D01A4 2.5%, #CEA9FD 43.47%, #5D01A4 60.21%, #CEA9FD 84.98%, #7E48C2 93.15%)',
    height: 0,
    paddingTop: '143.95%',

    '@supports (aspect-ratio: 91 / 131)': {
      aspectRatio: '91 / 131',
      height: 'unset',
      paddingTop: 0,
    },

    '> div': {
      position: 'absolute',
      inset: spacing.smx1,
      display: 'flex',
      flexDirection: 'column',
      background:
        'linear-gradient(256deg, #E3E3E3 12.03%, #FFF 22.04%, #E2E2E2 67.69%, #FFF 85.43%)',
      borderRadius: radius.md,
      boxShadow: 'inset 0 0 0.8rem rgba(0,0,0,0.5)',

      '> header': {
        position: 'relative',
        height: '38%',
        display: 'flex',
        flexDirection: 'column',

        '.profile-card__ribbon': {
          zIndex: layering.overlayHigh,
          position: 'absolute',
          insetInline: `calc((1.75rem) * -1)`,
          bottom: '-10%',
          display: 'flex',
          flexDirection: 'column',
          height: '5rem',
          transform: 'rotate(-11deg) skew(-11deg) scale(1.02)',
          background: 'linear-gradient(-11.41deg, #8A43C0 43.75%, #8A43C0 60.5769%, #5D01A4 100%)',
          borderRadius: `${spacing.sm} 0.1rem`,
          filter: 'drop-shadow(0px 0px 2px rgba(35, 35, 35, 0.66))',

          '--ribbon-edge-position': `calc((0.5rem - 0.06rem) * -1)`,

          '::before, ::after': {
            content: '""',
            position: 'absolute',
            background: '#47027D',
            width: '1rem',
            height: '0.5rem',
          },

          '::before': {
            left: 0,
            bottom: 'var(--ribbon-edge-position, 0)',
            clipPath: 'polygon(100% 1%, 1% 0, 100% 100%)',
          },

          '::after': {
            right: 0,
            top: 'var(--ribbon-edge-position, 0)',
            clipPath: 'polygon(0 0, 0 95%, 100% 95%)',
          },

          h4: {
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0.3rem',
            margin: 0,
            textShadow:
              '-2px -2px 0 #692E96, 2px -2px 0 #692E96, -2px 2px 0 #692E96, 2px 2px 0 #692E96, 0 0 1px #692E96',
            background:
              'linear-gradient(86deg, #5D01A4 -0.99%, #8A43C0 42.99%, #8A43C0 59.91%, #5D01A4 99.54%)',
            backgroundClip: 'content-box',
            borderRadius: 'inherit',
            color: colors.white,
            textAlign: 'center',
            fontSize: font.size.xl8.fontSize,
            letterSpacing: font.size.xl8.letterSpacing,
            fontWeight: font.weight.black,
            lineHeight: 1,
            fontFamily: 'Sofia Sans Extra Condensed',
            fontStyle: 'italic',

            '> span': {
              padding: `0.2rem ${spacing.smx2}`,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },

            [`&.${LocalConstants.ProfileCardDisplayNameXs}`]: { fontSize: font.size.xl8.fontSize },
            [`&.${LocalConstants.ProfileCardDisplayNameSm}`]: { fontSize: font.size.xl7.fontSize },
            [`&.${LocalConstants.ProfileCardDisplayNameMd}`]: { fontSize: font.size.xl5.fontSize },
            [`&.${LocalConstants.ProfileCardDisplayNameLg}`]: { fontSize: font.size.xl4.fontSize },
            [`&.${LocalConstants.ProfileCardDisplayNameXl}`]: { fontSize: font.size.xl3.fontSize },
          },
        },
      },

      '.profile-card__seal': {
        position: 'absolute',
        right: '-1.2rem',
        top: '-1.2rem',
        zIndex: layering.overlayHigh,
        width: '8rem',
        height: '8rem',

        '> img': { objectFit: 'cover', width: '100%', maxWidth: '100%', height: 'auto' },
      },

      '.profile-card__provided-sticker': {
        display: 'flex',
        justifyContent: 'flex-end',

        '> div': {
          display: 'flex',
          flexDirection: 'column',
          color: '#797878',
          marginTop: '-4%',
          width: '30%',
          padding: '2%',
          alignItems: 'center',
          backgroundColor: 'white',
          borderRadius: '5%',
          boxShadow: '0px 0px 3px 3px #FFF, 0px 0px 8px 5px rgba(0, 0, 0, 0.09) inset',
          clipPath: 'inset(-5px 3px -5px -5px)',
          marginRight: '-3px',

          '& span:first-of-type': {
            fontFamily: font.family.primary,
            margin: 0,
            marginTop: '1rem',
            fontSize: '0.8rem',
            padding: 0,
            fontWeight: font.weight.semibold,
            marginBottom: '-2px',
          },

          '& span:nth-of-type(2)': {
            textAlign: 'center',
          },
        },
      },

      '.profile-card__stats': {
        flex: 1,
        display: 'flex',
      },
    },
  },
})
